import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import dayjs from "dayjs";
import { Typography } from "@mui/material";
import { db, rateCardsCollection } from "../../../../services/firebase";
import { getNextRateCardId } from "../../../../services/countersService";
import { useServices } from "../../../../hooks/useServices";
import { getFirstWord } from "../../../../utils/utils";
import {
  updateServicesWithRates,
  updateUsersWithRates,
} from "../rate-cards-utils";
import RateCardForm from "../rate-card-form/rate-card-form";
import { CreateRateStyled } from "./create-rate.style";

const CreateRateCard = ({ initialData, onClose, onSuccess }) => {
  const { data: services = [], isLoading } = useServices();
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const makeDefaultValues = () => {
    // If editing: sort the incoming groups & their roles
    if (initialData) {
      const sortedGroups = [...initialData.serviceGroups]
        .sort((a, b) =>
          getFirstWord(a.name).localeCompare(getFirstWord(b.name))
        )
        .map((g) => ({
          ...g,
          roles: [...g.roles].sort((a, b) =>
            getFirstWord(a.name).localeCompare(getFirstWord(b.name))
          ),
        }));

      const initialFlat =
        initialData.rateOption === "flat"
          ? // if flat, show the very first role’s rate
            sortedGroups[0].roles[0].rate.toString()
          : "";

      return {
        name: initialData.name,
        rateOption: initialData.rateOption || "role",
        flatRate: initialFlat,
        isDefault: initialData.isDefault ?? false,
        serviceGroups: sortedGroups,
      };
    }

    // If new: sort the fetched services & each roles array
    const sortedServices = [...services].sort((a, b) =>
      getFirstWord(a.name).localeCompare(getFirstWord(b.name))
    );

    return {
      name: "",
      rateOption: "role",
      flatRate: "",
      isDefault: false,
      serviceGroups: sortedServices.map((g) => ({
        id: g.id,
        name: g.name,
        roles: [...g.roles]
          .sort((a, b) =>
            getFirstWord(a.name).localeCompare(getFirstWord(b.name))
          )
          .map((r) => ({ id: r.id, name: r.name, rate: "" })),
      })),
    };
  };

  // 🔥 ONE effect to:
  //   • fetch nextId (if new)
  //   • reset the form whenever services, nextId or initialData change
  const form = useForm({ defaultValues: makeDefaultValues() });
  const { handleSubmit, reset } = form;

  useEffect(() => {
    if (!isLoading) {
      reset(makeDefaultValues());
    }
  }, [services, isLoading, initialData, reset]);

  if (isLoading) {
    return <Typography>Loading…</Typography>;
  }

  const onSubmit = async (values) => {
    setSaving(true);

    // 1) Trim & dedupe name…
    const name = values.name.trim();
    const idField = initialData
      ? initialData.id
      : // ← only when creating do we pull the next ID
        await getNextRateCardId();

    const allSnap = await rateCardsCollection.get();
    const conflict = allSnap.docs.find((doc) => {
      const existing = String(doc.data().name || "")
        .trim()
        .toLowerCase();
      return existing === name.toLowerCase() && doc.id !== String(idField);
    });
    if (conflict) {
      alert(
        `Unable to create rate card. Another rate card with the same name already exists.`
      );
      setSaving(false);
      return;
    }

    // 2) Build payload
    const payload = {
      id: idField,
      name,
      rateOption: values.rateOption,
      // preserve original creation date on edits
      dateCreated: initialData
        ? initialData.dateCreated
        : dayjs().format("MM/DD/YYYY"),
      isDefault: !!values.isDefault,
      serviceGroups: values.serviceGroups.map((g) => ({
        id: g.id,
        name: g.name,
        roles: g.roles.map((r) => ({
          id: r.id,
          name: r.name,
          rate: values.flatRate ? Number(values.flatRate) : Number(r.rate),
        })),
      })),
    };

    // 3) Firestore batch
    const batch = db.batch();
    if (payload.isDefault) {
      const prev = await rateCardsCollection
        .where("isDefault", "==", true)
        .get();
      prev.docs.forEach((d) => batch.update(d.ref, { isDefault: false }));
    }
    batch.set(rateCardsCollection.doc(String(idField)), payload, {
      merge: true,
    });
    await batch.commit();

    // 4) Propagate defaults if needed
    if (payload.isDefault) {
      await updateServicesWithRates(payload.serviceGroups);
      await updateUsersWithRates(payload.serviceGroups);
    }

    // 5) Show your “Congrats!” view, then auto-close
    setJustSaved(true);
    setTimeout(() => {
      onSuccess();
      onClose();
    }, 2000);
  };

  return (
    <CreateRateStyled
      onConfirm={handleSubmit(onSubmit)}
      disableConfirmBtn={saving}
      confirmBtnColor="#77E250"
      closeBtnTitle="Cancel"
      noFooter={justSaved}
      onClose={onClose}
      confirmBtnTitle={initialData ? "Update" : "Save"}
    >
      {justSaved ? (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Typography variant="h5" gutterBottom>
            Congratulations!
          </Typography>
          <Typography>
            Your rate card was {initialData ? "updated" : "saved"} successfully.
          </Typography>
        </div>
      ) : (
        <>
          <div className="head">
            <Typography variant="h4">
              {initialData ? "Edit Rate Card" : "Add Rate Card"}
            </Typography>
          </div>
          <RateCardForm form={form} />
        </>
      )}
    </CreateRateStyled>
  );
};

export default CreateRateCard;
